import { useState, useMemo, useCallback } from 'react';
import { CustomerFilters, SortDirection } from '../models';

export const useCustomerFilters = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [ordersMinMonth, setOrdersMinMonth] = useState<number | null>(null);
  const [ordersMaxMonth, setOrdersMaxMonth] = useState<number | null>(null);
  const [saleType, setSaleType] = useState<'cod' | 'card' | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [customerOrigins, setCustomerOrigins] = useState<string[]>([]);

  // Memoize sorting object separately to maintain stable reference
  const sorting = useMemo(() => ({
    column: sortColumn,
    direction: sortDirection,
  }), [sortColumn, sortDirection]);

  // Build stable filter object with proper memoization
  const customerFilters: CustomerFilters = useMemo(() => {
    const filters: CustomerFilters = {
      page,
      per_page: perPage,
      sorting,
    };

    // Only add properties when they have meaningful values
    if (ordersMinMonth !== null) {
      filters.orders_prev_month_min = ordersMinMonth;
    }
    if (ordersMaxMonth !== null) {
      filters.orders_prev_month_max = ordersMaxMonth;
    }
    if (saleType) {
      filters.sale_type = saleType;
    }
    if (countryId !== null) {
      filters.country_id = countryId;
    }
    if (customerOrigins && customerOrigins.length > 0) {
      // Sort array for consistent comparison in React Query
      filters.customer_origin = [...customerOrigins].sort();
    }

    return filters;
  }, [page, perPage, sorting, ordersMinMonth, ordersMaxMonth, saleType, countryId, customerOrigins]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      ordersMinMonth !== null ||
      ordersMaxMonth !== null ||
      saleType ||
      countryId !== null ||
      (customerOrigins && customerOrigins.length > 0)
    );
  }, [ordersMinMonth, ordersMaxMonth, saleType, countryId, customerOrigins]);

  const updateFilters = useCallback((filters: {
    ordersMinMonth?: number | null;
    ordersMaxMonth?: number | null;
    saleType?: 'cod' | 'card' | null;
    countryId?: number | null;
    customerOrigins?: string[];
  }) => {
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0) {
      setPage(1);
    }

    if (filters.ordersMinMonth !== undefined) {
      setOrdersMinMonth(filters.ordersMinMonth);
    }
    if (filters.ordersMaxMonth !== undefined) {
      setOrdersMaxMonth(filters.ordersMaxMonth);
    }
    if (filters.saleType !== undefined) {
      setSaleType(filters.saleType);
    }
    if (filters.countryId !== undefined) {
      setCountryId(filters.countryId);
    }
    if (filters.customerOrigins !== undefined) {
      setCustomerOrigins(filters.customerOrigins);
    }
  }, []);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage !== undefined && newPerPage !== perPage) {
      setPerPage(newPerPage);
    }
  }, [perPage]);

  const updateSort = useCallback((column: string, direction: SortDirection) => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  const resetFilters = useCallback(() => {
    setPage(1);
    setPerPage(25);
    setSortColumn('id');
    setSortDirection('asc');
    setOrdersMinMonth(null);
    setOrdersMaxMonth(null);
    setSaleType(null);
    setCountryId(null);
    setCustomerOrigins([]);
  }, []);

  return {
    // Individual state values
    page,
    perPage,
    sortColumn,
    sortDirection,
    ordersMinMonth,
    ordersMaxMonth,
    saleType,
    countryId,
    customerOrigins,
    
    // Computed values
    customerFilters,
    hasActiveFilters,
    
    // Update functions with stable references
    updateFilters,
    updatePagination,
    updateSort,
    resetFilters,
  };
};